import { DatadogRUM } from '@user-portal/datadog-rum';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';

import isPrivateRoute from 'router/helpers/isPrivateRoute';

import api from 'api';
import { getToken, removeToken, setToken } from 'api/utils/tokenManagement';

import { handleLoginInstructionFeature, initAbTesting } from 'store/abTesting/sagas';
import { getDeepLink } from 'store/deepLink/sagas';
import { handleErrorAction } from 'store/errors/actions';
import { navigateTo } from 'store/navigation/actions';
import { notifyError } from 'store/notifications/actions';
import { resetSubscriptions } from 'store/subscriptions/actions';
import { resetUserData, setUserData } from 'store/user/actions';

import { HIDE_FRONT_CHAT_VALUE } from 'constants/frontChat';
import { PATHS } from 'constants/routerPaths';
import { GA_USER_ID } from 'constants/user';

import { clearUserDataFromAnalytics, setUserDataToAnalytics } from 'services/Analytics';
import { initFrontChat, shutdownFrontChat } from 'services/FrontChat';
import Sentry from 'services/Sentry/SentryInstance';

import { getCookie } from 'helpers/cookie';
import { getSubscriptionsPageUrl } from 'helpers/pathes';

import { UserStore } from 'types/store/userStore';

import {
    authenticationBySignature,
    authenticationFailed,
    authenticationSuccess,
    checkTemporaryPasswordRequest,
    generateTemporaryPasswordRequest,
    setAuthenticationStatus,
    setAuthRedirectUrl,
    signInError,
    signInRequest,
} from './actions';
import {
    AUTHENTICATE,
    AUTHENTICATE_BY_SIGNATURE,
    CHECK_TEMPORARY_PASSWORD_REQUEST,
    GENERATE_TEMPORARY_PASSWORD_REQUEST,
    LOG_OUT,
    SIGN_IN_REQUEST,
} from './actionTypes';
import { authRedirectURLSelector } from './selectors';

import { decagonClient } from '@/services/decagonClient';

function* generateTemporaryPassword({ payload }: ReturnType<typeof generateTemporaryPasswordRequest>) {
    const { email, onError, onSuccess } = payload;

    try {
        yield call(api.user.generateTemporaryPassword, payload);

        onSuccess && onSuccess();
    } catch (error) {
        yield put(notifyError('basics.appError'));
        yield put(handleErrorAction(error, { email }));

        onError && onError();
    }
}

function* checkTemporaryPassword({ payload }: ReturnType<typeof checkTemporaryPasswordRequest>) {
    const { email, password, onError, onSuccess } = payload;

    const gaUserId = getCookie(GA_USER_ID);

    try {
        const userDataResponse: UserStore = yield call(api.user.checkTemporaryPassword, {
            email,
            password,
            ...(gaUserId && { google_analytics_id: gaUserId }),
        });

        yield put(setAuthRedirectUrl(PATHS.MAIN));
        yield call(authorise, { userData: userDataResponse, authBeforeRedirect: true });

        onSuccess && onSuccess();
    } catch (error: any) {
        yield put(notifyError('basics.appError'));
        yield put(handleErrorAction(error, payload));

        onError && onError();
    }
}

function* signIn({ payload }: ReturnType<typeof signInRequest>) {
    const { userData, onError } = payload;

    try {
        const userDataResponse: UserStore = yield call(api.user.signIn, userData);

        yield put(setAuthRedirectUrl(PATHS.MAIN));

        yield call(authorise, { userData: userDataResponse, authBeforeRedirect: true });
    } catch (error: any) {
        yield put(notifyError('login.error'));
        yield put(handleErrorAction(error, payload));

        if (error?.error) yield put(signInError(error.error));

        onError && onError();
    }
}

function* authenticate() {
    const pathname = window.location?.pathname;

    const isPrivate = isPrivateRoute(pathname);

    yield put(setAuthRedirectUrl(isPrivate ? pathname : PATHS.MAIN));

    const token = getToken();

    if (!token) {
        yield put(setAuthenticationStatus(false));

        return;
    }

    try {
        const userData: UserStore = yield call(api.user.getUser);

        yield call(authorise, { userData });
    } catch (error: any) {
        removeToken();

        yield put(notifyError('basics.appError'));

        yield put(authenticationFailed());
        yield put(handleErrorAction(error));

        yield put(navigateTo({ url: PATHS.TEMPORARY_PASSWORD }));
    }
}

function* authenticateBySignature({ payload }: ReturnType<typeof authenticationBySignature>) {
    const { uId, signature } = payload;

    yield put(setAuthRedirectUrl(getSubscriptionsPageUrl()));

    try {
        const userData: UserStore = yield call(api.user.getUserBySignature, { uId, signature });

        yield call(authorise, { userData });
    } catch (error: any) {
        removeToken();

        yield put(notifyError('basics.appError'));
        yield put(authenticationFailed());
        yield put(handleErrorAction(error, payload));

        yield put(navigateTo({ url: PATHS.TEMPORARY_PASSWORD }));
    }
}

function* authorise({ userData, authBeforeRedirect }: { userData: UserStore; authBeforeRedirect?: boolean }) {
    userData?.token && setToken(userData.token);

    if (userData.user_id) {
        DatadogRUM.setUser(userData.user_id, userData.email);
    }

    Sentry.setUser({
        email: userData.email as string,
        user_id: userData.user_id as string,
    });

    setUserDataToAnalytics({
        id: userData.user_id ?? '',
        country: String(userData.country),
        gender: userData.gender,
        abTestName: userData.ab_test_name,
        region: userData.geo_region_code,
    });

    yield call(initAbTesting, userData);
    yield call(handleLoginInstructionFeature, userData.is_logged_in);

    if (authBeforeRedirect) {
        yield put(setAuthenticationStatus(true));
    }

    const redirectURL: string = yield select(authRedirectURLSelector);

    yield put(navigateTo({ url: redirectURL || PATHS.MAIN }));

    yield put(setUserData(userData));

    yield call(getDeepLink);

    yield delay(300);

    yield put(authenticationSuccess());

    const { name, email, language } = userData;

    if (import.meta.env.VITE_HIDE_FRONT_CHAT !== HIDE_FRONT_CHAT_VALUE) {
        initFrontChat({ name, email: String(email), language: String(language) });
    }

    yield put(setAuthRedirectUrl(null));
}

function* logout() {
    const wasValidSession = !!getToken();

    clearUserDataFromAnalytics();

    removeToken();

    yield call(DatadogRUM.clearUser);
    Sentry.clearUser();

    decagonClient.hide();

    yield put(setAuthenticationStatus(false));
    yield put(resetUserData());
    yield put(resetSubscriptions());

    yield put(navigateTo({ url: PATHS.TEMPORARY_PASSWORD }));

    shutdownFrontChat();

    if (wasValidSession) {
        // TODO MIGRATION LOGIC
        window.location.reload();
    }
}

export default function* watchAuth() {
    yield all([
        takeLatest(LOG_OUT, logout),
        takeLatest(SIGN_IN_REQUEST, signIn),
        takeLatest(AUTHENTICATE, authenticate),
        takeLatest(AUTHENTICATE_BY_SIGNATURE, authenticateBySignature),
        takeLatest(GENERATE_TEMPORARY_PASSWORD_REQUEST, generateTemporaryPassword),
        takeLatest(CHECK_TEMPORARY_PASSWORD_REQUEST, checkTemporaryPassword),
    ]);
}
